import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/design-system/badge';
import { RiArrowRightSLine } from '@remixicon/react';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import {
  backOfficeDriverUrl,
  type CodReconRiderRow,
  type CodReconRidersParams,
  getCodReconRiders,
} from '@/services/api/codOrders';
import { formatRupiah, formatTimeInStatus } from './CodBadges';

/**
 * The Bulk Reconciliation view: one row per rider holding unsettled cash.
 *
 * The board answers "where is this order's money". This answers "how much is
 * outstanding against this rider, and can I settle it in one go" — today that
 * means six drawers and six typed amounts for one rider's six orders.
 *
 * ONE TABLE, both stages side by side, because a rider group routinely holds
 * orders at both: three handed to the PIC this morning, two still in the
 * rider's pocket. Splitting them into two tables would split one rider across
 * two rows and lose the only figure the operator actually chases — the total
 * that rider is responsible for.
 *
 * The per-stage split reappears inside the modal, where it has to: a single
 * bulk action cannot span two legs (§2).
 */
const CodBulkReconPanel: React.FC<{
  params: CodReconRidersParams;
  /** Row click → the rider's detail drawer, which is where both confirm
   *  actions now live. Null riderId never gets here. */
  onOpenRider: (row: CodReconRiderRow) => void;
  /** Bumped by the parent after a successful bulk, to force a refetch. */
  refreshToken?: number;
}> = ({ params, onOpenRider, refreshToken = 0 }) => {
  const [result, setResult] = useState<{
    data: CodReconRiderRow[];
    totalLeg1Amount: number;
    totalLeg2Amount: number;
    totalReceivableAmount: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  const { clientId, search, completedStartDate, completedEndDate } = params;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    getCodReconRiders({
      clientId,
      search,
      completedStartDate,
      completedEndDate,
    })
      .then((res) => {
        if (!cancelled) setResult(res);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        toast.error(error.message);
        // Cleared rather than left stale: these are money figures, and showing
        // the previous filter's totals under the current filter's heading is
        // worse than showing nothing.
        setResult(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Cancelled on re-filter so a slow first request cannot land after a fast
    // second one and repaint the table with the wrong scope.
    return () => {
      cancelled = true;
    };
  }, [clientId, search, completedStartDate, completedEndDate, refreshToken]);

  /**
   * Sorting is DELEGATED, not left to TableData's internal comparator.
   *
   * That one does `record[field]`, which cannot express two of the five
   * columns: `total` is derived (`leg1Amount + leg2Amount`) and has no field,
   * and `oldestStageStartedAt` needs elapsed time so the arrow matches the
   * duration the cell displays rather than the timestamp behind it. It also
   * has no null policy — the "Belum ada rider" group would float to the top of
   * a name sort, which is the one row that can never be acted on.
   */
  const [sort, setSort] = useState<{
    field: string;
    direction: 'asc' | 'desc' | null;
  }>({ field: 'total', direction: 'desc' });

  const fetched = useMemo(() => result?.data ?? [], [result]);

  /**
   * Order counts for the totals band.
   *
   * Summed here rather than asked of the server because the endpoint returns
   * every group (this view is deliberately not paginated), so the client's sum
   * is the same number a second aggregate query would produce — and it stays
   * correct while a filter narrows the set.
   */
  const orderTotals = useMemo(
    () =>
      fetched.reduce(
        (acc, r) => ({
          leg1Count: acc.leg1Count + r.leg1Count,
          leg2Count: acc.leg2Count + r.leg2Count,
        }),
        { leg1Count: 0, leg2Count: 0 }
      ),
    [fetched]
  );

  const rows = useMemo(() => {
    if (!sort.direction) return fetched;
    const dir = sort.direction === 'asc' ? 1 : -1;
    // Elapsed ms, so a bigger number is an older order — the same order the
    // "Tertua" cell reads in. Null (no stage timestamp) sorts as youngest.
    const age = (r: CodReconRiderRow) =>
      r.oldestStageStartedAt
        ? Date.now() - new Date(r.oldestStageStartedAt).getTime()
        : -1;
    const value = (r: CodReconRiderRow): number | string => {
      switch (sort.field) {
        case 'rider':
          return (r.riderName ?? r.riderCode ?? '').toLowerCase();
        case 'leg1':
          return r.leg1Amount;
        case 'leg2':
          return r.leg2Amount;
        case 'receivable':
          // Debts are negative, so ascending puts the biggest one first. The
          // absolute value is what an operator ranks by — "who owes the most" —
          // and the sign is already carried by the colour in the cell.
          return Math.abs(r.receivableAmount);
        case 'age':
          return age(r);
        default:
          return r.leg1Amount + r.leg2Amount;
      }
    };
    return [...fetched].sort((a, b) => {
      // The no-rider group sinks under every sort and both directions: it is
      // the one row with no action, so it never deserves the top.
      if (a.riderId === null) return 1;
      if (b.riderId === null) return -1;
      const av = value(a);
      const bv = value(b);
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * dir;
    });
  }, [fetched, sort]);

  /**
   * H3 — names and codes that appear on more than one row.
   *
   * Grouping is on the rider id (correct), but the display name and code come
   * from the newest snapshot in each group and snapshots drift. Live data has
   * `Budi Santoso` twice and `MTR0006903` twice, on different riders holding
   * different money. Rendering that collision without a disambiguator makes
   * two rows look like one person with two balances — on a money screen that
   * is worse than showing a raw id.
   *
   * Computed over the result set rather than always shown: an id appended to
   * every row is noise on the common case where names are unique.
   */
  const ambiguous = useMemo(() => {
    const names = new Map<string, number>();
    const codes = new Map<string, number>();
    for (const r of rows) {
      if (r.riderName)
        names.set(r.riderName, (names.get(r.riderName) ?? 0) + 1);
      if (r.riderCode)
        codes.set(r.riderCode, (codes.get(r.riderCode) ?? 0) + 1);
    }
    return {
      names: new Set([...names].filter(([, n]) => n > 1).map(([k]) => k)),
      codes: new Set([...codes].filter(([, n]) => n > 1).map(([k]) => k)),
    };
  }, [rows]);

  const columns = useMemo<TableDataColumn<CodReconRiderRow>[]>(
    () => [
      {
        key: 'rider',
        sortable: true,
        title: 'Rider',
        render: (_v, row) => {
          if (row.riderId === null) {
            // Shown, never hidden: these orders carry money too, and a view
            // whose totals do not add up to the board's cards is a bug report
            // waiting to happen. Not actionable — nobody is on record as
            // having collected it, so there is no one to settle with.
            return (
              <div className="flex flex-col gap-0.5">
                <span className="text-text-sub-600">Belum ada rider</span>
                <span className="text-xs text-text-sub-600">
                  Tidak bisa dikonfirmasi massal
                </span>
              </div>
            );
          }
          const url = backOfficeDriverUrl(row.riderId);
          return (
            <div className="flex flex-col">
              {url ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-accent hover:text-accent-dark hover:underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {row.riderName ?? row.riderCode}
                </a>
              ) : (
                <span className="font-medium">{row.riderName ?? '—'}</span>
              )}
              {row.riderCode && (
                <span className="text-xs text-text-sub-600">
                  {row.riderCode}
                  {/* H3 — only when this code or name is not unique in the
                      result set. The id is the thing that actually
                      distinguishes them, so it is what gets shown. */}
                  {(ambiguous.codes.has(row.riderCode) ||
                    (row.riderName && ambiguous.names.has(row.riderName))) && (
                    <span className="ml-1 text-text-sub-600">
                      · ID {row.riderId}
                    </span>
                  )}
                </span>
              )}
            </div>
          );
        },
      },
      {
        key: 'total',
        sortable: true,
        title: 'Total dipegang',
        render: (_v, row) => (
          // M2 — the sort key, so it leads rather than sitting between its own
          // operands. Bold because it is the one number that decides whether
          // the row gets opened; the two stage columns after it are the
          // breakdown, not competitors.
          <span className="font-medium text-text-strong-950">
            {formatRupiah(row.leg1Amount + row.leg2Amount)}
          </span>
        ),
      },
      {
        key: 'leg1',
        sortable: true,
        title: 'Rider → PIC',
        render: (_v, row) => (
          <StageCell count={row.leg1Count} amount={row.leg1Amount} />
        ),
      },
      {
        key: 'leg2',
        sortable: true,
        title: 'PIC → Finance',
        render: (_v, row) => (
          <StageCell count={row.leg2Count} amount={row.leg2Amount} />
        ),
      },
      {
        key: 'receivable',
        sortable: true,
        // Its own column, after the two custody stages and before the ageing
        // clock. Those two are "what this rider is holding right now"; this is
        // "what they still owe", which survives the orders that created it —
        // so a rider can read 0 / 0 here and still show a figure.
        title: 'Piutang',
        render: (_v, row) =>
          row.receivableAmount === 0 ? (
            <span className="text-text-soft-400">—</span>
          ) : (
            <span
              className={
                row.receivableAmount < 0
                  ? 'text-error-base'
                  : 'text-success-base'
              }
            >
              <span className="block">
                {/* MAGNITUDE, not the raw signed figure. `receivableAmount` is
                    a sum of `discrepancy_amount`, where a shortfall is
                    NEGATIVE — so a rider owing fifty thousand printed
                    "Rp -50.000" under a column headed "Piutang". A debt of
                    fifty thousand is fifty thousand; the minus was the storage
                    convention leaking onto the screen, and it reads like the
                    opposite of what it means. The rare positive case (a rider
                    charged for handing over MORE than the order) says so in
                    words rather than relying on a sign nobody parses. */}
                {formatRupiah(Math.abs(row.receivableAmount))}
              </span>
              <span className="block text-xs text-text-sub-600">
                {row.receivableAmount > 0 && 'kelebihan · '}
                {row.receivableOrders} pesanan
              </span>
            </span>
          ),
      },
      {
        key: 'age',
        sortable: true,
        title: 'Tertua',
        render: (_v, row) =>
          row.oldestStageStartedAt ? (
            <span className="text-sm text-text-sub-600">
              {formatTimeInStatus(row.oldestStageStartedAt)}
            </span>
          ) : (
            <span className="text-text-sub-600">—</span>
          ),
      },
      {
        key: 'open',
        title: '',
        // The Confirm buttons that used to live here are gone. Two money
        // actions per row put a 230px column of identical buttons beside the
        // data, pushed the table into horizontal scroll, and let a
        // single-button row render its action in the other leg's slot.
        //
        // A chevron says the row opens something and nothing more. The two
        // actions now live in the drawer footer, where they are full-size,
        // labelled, and sit under the orders they apply to.
        render: (_v, row) =>
          row.riderId === null ? null : (
            <span className="flex justify-end text-icon-sub-600 group-hover:text-text-strong-950">
              <RiArrowRightSLine aria-hidden className="size-5" />
            </span>
          ),
      },
    ],
    [ambiguous.codes, ambiguous.names]
  );

  const leg1Amount = result?.totalLeg1Amount ?? 0;
  const leg2Amount = result?.totalLeg2Amount ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <ReconTotalsBand
        riderCount={rows.length}
        orderCount={orderTotals.leg1Count + orderTotals.leg2Count}
        leg1Count={orderTotals.leg1Count}
        leg1Amount={leg1Amount}
        leg2Count={orderTotals.leg2Count}
        leg2Amount={leg2Amount}
        receivableAmount={result?.totalReceivableAmount ?? 0}
        loading={loading}
      />

      <TableData
        data={rows}
        columns={columns}
        loading={loading}
        sorting={{
          field: sort.field,
          direction: sort.direction,
          onSort: (field, direction) => setSort({ field, direction }),
        }}
        onRowClick={(row) => {
          if (row.riderId === null) return;
          onOpenRider(row);
        }}
      />
    </div>
  );
};

/** Count and money for one custody stage. Zero reads as a dash, not "Rp 0" —
 *  "nothing here" and "nothing owed" are the same thing at a glance, and a
 *  column of Rp 0 makes the stage that DOES hold money harder to find. */
const StageCell: React.FC<{ count: number; amount: number }> = ({
  count,
  amount,
}) => {
  if (count === 0) return <span className="text-text-sub-600">—</span>;
  return (
    <div className="flex items-center gap-2">
      {/* M8 — 12px, the caption floor. The Badge default is 11px. */}
      <Badge status="faded" className="text-xs">
        {count} pesanan
      </Badge>
      <span className="text-sm text-text-strong-950">
        {formatRupiah(amount)}
      </span>
    </div>
  );
};

/**
 * The totals band.
 *
 * This is the most important information on the tab and it used to read as
 * three pieces of label text at the same weight as the filter bar above it — a
 * reader scanning for "how much is out there" had to stop and parse. So the
 * money is set large, the two custody stages are the visual subject, and the
 * counts ride along as captions instead of competing for their own slots.
 *
 * The grand total is derived, not fetched: it is the only figure that answers
 * "how much is unsettled in total", and the two stages are its parts. Showing
 * the parts without the whole made the reader add two seven-digit numbers in
 * their head.
 *
 * The two stage figures keep their reconciliation tooltips (M6/P1): they are a
 * property to check once against the board's cards, not a sentence to re-read
 * on every visit.
 */
const ReconTotalsBand: React.FC<{
  riderCount: number;
  orderCount: number;
  leg1Count: number;
  leg1Amount: number;
  leg2Count: number;
  leg2Amount: number;
  receivableAmount: number;
  loading: boolean;
}> = ({
  riderCount,
  orderCount,
  leg1Count,
  leg1Amount,
  leg2Count,
  leg2Amount,
  receivableAmount,
  loading,
}) => (
  <div className="overflow-hidden rounded-xl border border-stroke-soft-200 bg-bg-white-0">
    <div className="flex flex-wrap items-stretch divide-stroke-soft-200 md:divide-x">
      {/* The whole, first — everything to its right is a part of it. */}
      <div className="flex min-w-[13rem] flex-1 flex-col justify-center gap-1 px-4 py-3">
        <span className="text-xs uppercase tracking-wide text-text-sub-600">
          Belum disetor
        </span>
        <span
          className={`whitespace-nowrap text-2xl font-semibold tabular-nums text-text-strong-950 ${
            loading ? 'opacity-40' : ''
          }`}
        >
          {formatRupiah(leg1Amount + leg2Amount)}
        </span>
        <span className="text-xs text-text-sub-600">
          {orderCount} pesanan · {riderCount} rider
        </span>
      </div>

      <StageTotal
        label="Di rider"
        legend="Leg 1 · rider → PIC"
        count={leg1Count}
        amount={leg1Amount}
        loading={loading}
        title="Harus sama dengan kartu Outstanding di Status board"
      />
      <StageTotal
        label="Di PIC"
        legend="Leg 2 · PIC → Finance"
        count={leg2Count}
        amount={leg2Amount}
        loading={loading}
        title="Harus sama dengan kartu Pending Deposit di Status board"
      />
      {/* Last, and deliberately outside the "Belum disetor" whole to its left.
          Everything before it is money somebody is holding right now; this is
          money already accounted for that a rider still owes, and it spans
          orders that are long since Selesai. Adding it into the total would
          double-count the ones that overlap and invent the ones that do not. */}
      {receivableAmount !== 0 && (
        <StageTotal
          label="Piutang rider"
          legend={
            receivableAmount > 0
              ? 'Kelebihan yang ditagihkan'
              : 'Selisih yang ditagihkan'
          }
          count={null}
          // Magnitude, same reason as the column — see the Piutang cell.
          amount={Math.abs(receivableAmount)}
          loading={loading}
          title="Lintas semua pesanan, termasuk yang sudah Selesai — bukan bagian dari Belum disetor"
        />
      )}
    </div>
  </div>
);

/** One custody stage inside the totals band. */
const StageTotal: React.FC<{
  label: string;
  legend: string;
  /** Null when the figure has no order count to quote — the receivable total
   *  spans orders that are no longer in this view. */
  count: number | null;
  amount: number;
  loading: boolean;
  title: string;
}> = ({ label, legend, count, amount, loading, title }) => (
  <div
    className="flex min-w-[13rem] flex-1 flex-col justify-center gap-1 px-4 py-3"
    title={title}
  >
    <span className="text-xs uppercase tracking-wide text-text-sub-600">
      {label}
    </span>
    <span
      className={`whitespace-nowrap text-xl font-medium tabular-nums text-text-strong-950 ${
        loading ? 'opacity-40' : ''
      }`}
    >
      {/* Zero reads as a dash for the same reason StageCell does it per row:
          "nothing here" and "Rp 0" are the same fact, and the dash keeps the
          stage that DOES hold money easier to find. */}
      {count === 0 ? '—' : formatRupiah(amount)}
    </span>
    <span className="text-xs text-text-sub-600">
      {count === null || count === 0 ? legend : `${count} pesanan · ${legend}`}
    </span>
  </div>
);

export default CodBulkReconPanel;
